<template>
  <cui-billable-usage-widget
    :period="period"
    :periods="periods"
    :rows="rows"
    :loading="usage.loading"
    :error="!!usage.error"
    :calculated="calculated"
    :export-disabled="exportDisabled"
    :labels="labels"
    @update:period="onPeriodChange"
    @retry="onRetry"
    @export="onExport"
  />
</template>

<script lang="ts">
import { computed, defineComponent, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import {
  BillableUsageStatus,
  CuiBillableUsageWidget,
  mapBillableUsageItems,
  type BillableProduct,
  type CuiBillableUsageWidgetLabels,
} from "@coronet/cui";
import { useMspBillableUsageStore } from "@/_store/msp/msp-billable-usage.module.ts";
import { useMspBillingUsageExport } from "@/composables/useMspBillingUsageExport.ts";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { i18n } from "@/plugins/i18n.ts";
import { BILLABLE_PRODUCT_LABEL_KEYS } from "@/constants/billable-usage.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export default defineComponent({
  components: { CuiBillableUsageWidget },
  props: {
    workspaceId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const store = useMspBillableUsageStore();
    const { usage, period, periods } = storeToRefs(store);
    const { openBillingUsageExport } = useMspBillingUsageExport();

    const productLabels = computed(
      () =>
        Object.fromEntries(
          Object.entries(BILLABLE_PRODUCT_LABEL_KEYS).map(([product, key]) => [product, t(key)])
        ) as Record<BillableProduct, string>
    );
    // cui keeps the backend order; the rows are shown alphabetically by their translated names.
    const rows = computed(() =>
      mapBillableUsageItems(usage.value.data?.items ?? [], {
        productLabels: productLabels.value,
        legacySuffix: t("mspWorkspacePreview.billableUsage.legacySuffix"),
      }).sort((a, b) => a.name.localeCompare(b.name, i18n.global.locale.value))
    );
    const calculated = computed(
      () => usage.value.data?.status !== BillableUsageStatus.NOT_CALCULATED
    );
    const exportDisabled = computed(() =>
      isMspActionRestricted(
        MspRolePermissionScopes.MSP_PORTAL_SCOPE,
        MspPortalScopeSection.MSP_REPORTS
      )
    );
    const labels = computed((): CuiBillableUsageWidgetLabels => ({
      title: t("mspWorkspacePreview.billableUsage.title"),
      exportCsv: t("general.exportCsv"),
      productColumn: t("mspWorkspacePreview.billableUsage.productColumn"),
      workspacesBilledColumn: t("mspWorkspacePreview.billableUsage.workspacesBilledColumn"),
      billableUnitsColumn: t("mspWorkspacePreview.billableUsage.billableUnitsColumn"),
      emptyTitle: t("mspWorkspacePreview.billableUsage.emptyTitle"),
      emptySubtitle: t("mspWorkspacePreview.billableUsage.emptySubtitle"),
      noUsageTitle: t("mspWorkspacePreview.billableUsage.noUsageTitle"),
      noUsageSubtitle: t("mspWorkspacePreview.billableUsage.noUsageSubtitle"),
      errorLabel: t("mspWorkspacePreview.billableUsage.errorLabel"),
      retryLabel: t("general.retry"),
      viewAll: t("general.viewAll"),
      viewLess: t("general.viewLess"),
    }));

    const onPeriodChange = (newPeriod: string) => {
      store.getBillableUsage(props.workspaceId, newPeriod);
    };

    const onRetry = () => {
      if (period.value) {
        store.getBillableUsage(props.workspaceId, period.value);
        return;
      }
      store.loadBillableUsage(props.workspaceId);
    };

    const onExport = () => {
      openBillingUsageExport({
        workspaceId: props.workspaceId,
        period: period.value,
      });
    };

    watch(
      () => props.workspaceId,
      (workspaceId) => {
        store.loadBillableUsage(workspaceId);
      },
      { immediate: true }
    );

    return {
      usage,
      period,
      periods,
      rows,
      calculated,
      exportDisabled,
      labels,
      onPeriodChange,
      onRetry,
      onExport,
    };
  },
});
</script>
